import { sumBy } from "lodash";
import { useMemo, type FC } from "react";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useRootSelector } from "@ds/hooks";

import { AggregatedValue } from "@ds/components/entities";
import { InfoAttribute, InfoSection } from "@ds/components/info-view";
import { TrimmedContent } from "@ds/components/trimmed-text";
import { formatAbsoluteDate, formatRelativeDate } from "@ds/utils/date";
import { getProperty } from "@ds/utils/properties";

import { selectDeploymentsByIds } from "../../redux/selectors";
import { ChipCount } from "./chip-count";
import { getDeploymentStatusIndicator } from "./status-indicator";

export const DeploymentInfoSection: FC<{ deployment: Deployment }> = ({ deployment }) => {
  const aggregatedDeploymentIds = useGetAggregatedEntityIds(deployment);
  const aggregatedDeployments = useRootSelector(state => selectDeploymentsByIds(state, aggregatedDeploymentIds));

  const numberOfPlayers = useMemo(() => {
    const totalCount = sumBy(aggregatedDeployments, "total_count");
    const succeededCount = sumBy(aggregatedDeployments, "succeeded_count");
    const failedCount = sumBy(aggregatedDeployments, "failed_count");
    const offlineCount = sumBy(aggregatedDeployments, "offline_count");
    const executingCount = sumBy(aggregatedDeployments, "executing_count");
    const inFlightCount = succeededCount + failedCount + offlineCount + executingCount;

    return (
      <>
        <TrimmedContent content="Total">
          <ChipCount label={`${totalCount}`} $isSmall />
        </TrimmedContent>
        {inFlightCount > 0 && <span>:&nbsp;&nbsp;</span>}
        {succeededCount > 0 && (
          <TrimmedContent content="Succeeded">
            <ChipCount label={`${succeededCount}`} $isSmall $isSucceeded />
          </TrimmedContent>
        )}
        {failedCount > 0 && (
          <TrimmedContent content="Failed">
            <ChipCount label={`${failedCount}`} $isSmall $isFailed />
          </TrimmedContent>
        )}
        {offlineCount > 0 && (
          <TrimmedContent content="Offline">
            <ChipCount label={`${offlineCount}`} $isSmall $isOffline />
          </TrimmedContent>
        )}
        {executingCount > 0 && (
          <TrimmedContent content="Executing">
            <ChipCount label={`${executingCount}`} $isSmall $isExecuting />
          </TrimmedContent>
        )}
      </>
    );
  }, [aggregatedDeployments]);

  return (
    <InfoSection title="Deployment Info" icon="pi pi-info-circle">
      <InfoAttribute
        name="Status"
        value={
          <AggregatedValue
            entity={deployment}
            field="status"
            formatter={entity => getDeploymentStatusIndicator(entity)}
          />
        }
      />

      <InfoAttribute name="Message" value={<AggregatedValue entity={deployment} field="message" />} />
      <InfoAttribute
        name="Schedule"
        value={
          <AggregatedValue
            entity={deployment}
            field="params.scheduled_time"
            formatter={(entity, field) => formatAbsoluteDate(getProperty(entity, field))}
          />
        }
      />

      <InfoAttribute name="Version" value={<AggregatedValue entity={deployment} field="version" />} />

      <InfoAttribute
        name="Started"
        value={
          <AggregatedValue
            entity={deployment}
            field="started_at"
            formatter={(entity, field) => formatRelativeDate(getProperty(entity, field))}
          />
        }
      />

      <InfoAttribute
        name="Ended"
        value={
          <AggregatedValue
            entity={deployment}
            field="ended_at"
            formatter={(entity, field) => formatRelativeDate(getProperty(entity, field))}
          />
        }
      />

      <InfoAttribute name="# Players" value={numberOfPlayers} />
    </InfoSection>
  );
};
